import { generatePath } from '@/services/router/generate-path';
import { useMemo } from 'react';
import {
  Badge,
  DataTable,
  Drawer,
  DrawerBody,
  DrawerInset,
  Icon,
  Text,
  Link,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  Stack,
  SkeletonText,
  FormattedRelativeTime,
  Tooltip,
  PanelActions,
} from '@customerio/pluma-components/react';
import { createColumnHelper } from '@tanstack/table-core';
import { useAnalytics } from '@/services/analytics';
import { useEnvHealthCampaigns, useWorkspaceHealthSimulation } from '@/features/workspace-health';
import type { ServicesApiEnvHealthStaleCampaign } from '@/services/api/services-api';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useActiveHealthTab, useTableDrawer } from '../hooks';

const columnHelper = createColumnHelper<ServicesApiEnvHealthStaleCampaign>();

export function CampaignsPanel() {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { track } = useAnalytics();
  const { activeTab } = useActiveHealthTab();
  const { simulate } = useWorkspaceHealthSimulation();
  const { isDrawerOpen, setIsDrawerOpen } = useTableDrawer('campaigns');

  const campaignsQuery = useEnvHealthCampaigns(workspaceId, simulate, activeTab === 'messaging');

  const data = campaignsQuery.data;
  const activeCount = data?.active_count ?? 0;
  const staleCampaigns = data?.stale ?? [];
  const staleCount = data?.stale_count ?? 0;
  const processedToday = data?.processed_today ?? 0;

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Campaign',
        cell: (info) => (
          <Link
            href={generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId', {
              workspaceId,
              campaignId: info.row.original.id,
            })}
            variant="secondary"
            isUnderlined={false}
          >
            <Text size="s">{info.getValue()}</Text>
          </Link>
        ),
      }),
      columnHelper.accessor('last_sent_at', {
        header: 'Last sent',
        cell: (info) => {
          const value = info.getValue();
          if (value != null && value > 0) {
            return <FormattedRelativeTime date={new Date(value * 1000)} size="xs" color="subtle" />;
          }
          return (
            <Text size="xs" color="subtle">
              Over 90 days ago
            </Text>
          );
        },
      }),
    ],
    [workspaceId],
  );

  return (
    <>
      <Panel style={{ height: '100%' }}>
        <PanelHeader>
          <PanelTitle>Campaigns</PanelTitle>
          <PanelActions>
            <Link
              href={generatePath('/workspaces/:workspaceId/journeys/campaigns', { workspaceId })}
              variant="primary"
              onClick={() => void track('workspaceHealth', 'viewAllClicked', { Destination: 'campaigns' })}
            >
              View all
            </Link>
          </PanelActions>
        </PanelHeader>
        <PanelSection>
          {campaignsQuery.isLoading ? (
            <SkeletonText size="s" lines={3} />
          ) : (
            <Stack gap="100">
              <InlineStack alignInline="space-between" alignBlock="center" py="050">
                <Text size="s">Active</Text>
                <Badge>{activeCount.toLocaleString()}</Badge>
              </InlineStack>
              <InlineStack alignInline="space-between" alignBlock="center" py="050">
                <Text size="s">
                  No recent sends{' '}
                  {staleCount > 0 ? (
                    <Link
                      onClick={() => {
                        void track('workspaceHealth', 'drawerOpened', { Drawer: 'campaigns' });
                        setIsDrawerOpen('campaigns');
                      }}
                      variant="secondary"
                    >
                      (view)
                    </Link>
                  ) : null}
                </Text>
                <Badge>{staleCount.toLocaleString()}</Badge>
              </InlineStack>
              <InlineStack alignInline="space-between" alignBlock="center" py="050">
                <InlineStack gap="050" alignBlock="center">
                  <Text size="s">Processed today</Text>
                  <Tooltip content="Total campaign executions today, including starts, continuations, and retries. This number typically scales with the number of active campaigns.">
                    <Icon name="help" size="xs" color="subtle" />
                  </Tooltip>
                </InlineStack>
                <Badge>{processedToday.toLocaleString()}</Badge>
              </InlineStack>
            </Stack>
          )}
        </PanelSection>
      </Panel>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(null)} title="Inactive campaigns">
        <DrawerBody>
          <Text size="s" color="subtle">
            These campaigns are active but haven&apos;t sent any messages in the last 30 days. Consider stopping or
            archiving them to free up processing capacity.
          </Text>
          <DrawerInset pt="200" px="050">
            <DataTable data={staleCampaigns} columns={columns} caption="Inactive campaigns" withSorting={true} />
          </DrawerInset>
        </DrawerBody>
      </Drawer>
    </>
  );
}
